import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';

export const COURSE_TITLE_MAX_LENGTH = 160;
export const COURSE_SUMMARY_MAX_LENGTH = 600;

/** Per-locale course copy (N5, W7). Both locales are required — U8's parity law. */
export const CourseTranslationSchema = z
  .object({
    title: z.string().trim().min(1).max(COURSE_TITLE_MAX_LENGTH),
    /** The catalog card's line. Long enough to say what the course covers. */
    summary: z.string().trim().min(1).max(COURSE_SUMMARY_MAX_LENGTH),
  })
  .strict();

export type CourseTranslation = z.infer<typeof CourseTranslationSchema>;

export const CourseTranslationsSchema = makeTranslationsSchema(CourseTranslationSchema);

/**
 * A staff-authored course (`03-courses-discounts.md`).
 *
 * `title` and `summary` are the **caller's locale**, resolved by the reader;
 * the admin edit form reads {@link CourseDetailSchema}, which carries every
 * locale (W7).
 *
 * **`isActive` is publication, and withdrawing one never destroys progress.** A
 * member who completed a course that was later deactivated keeps their
 * `course_completions` row and every grant it earned (matrix row 18) — the flag
 * governs what may be *started*, never what has already been earned.
 */
export const CourseSchema = z
  .object({
    id: UuidSchema,
    isActive: z.boolean(),
    /** Curriculum order in the catalog — staff-controlled, not alphabetical. */
    sortOrder: z.number().int().nonnegative(),
    lessonCount: z.number().int().nonnegative(),
    /**
     * The course's test, when it has one.
     *
     * At most one, because "the course test" is what a `test_pass` discount rule
     * names and what the catalog card offers; a course needing two assessments
     * is two courses. A **standalone** test has no course at all and is not
     * reachable from here (`tests.courseId` is null — matrix row 4).
     */
    testId: UuidSchema.nullable(),
    title: z.string(),
    summary: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Course = z.infer<typeof CourseSchema>;

/** What the admin edit form reads: every locale, not just the caller's (W7). */
export const CourseDetailSchema = CourseSchema.extend({
  translations: CourseTranslationsSchema,
}).strict();

export type CourseDetail = z.infer<typeof CourseDetailSchema>;
